import breakOut from '@sparta.global.utilities/sparta-breakout-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaMediaQueryUtility from '@sparta.global.utilities/sparta-mediaquery-utility/dist/es6.js';
import '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import spartaTabDropdownUtility from '@sparta.global.utilities/sparta-tab-dropdown-utility/dist/es6.js';

/**
 * Namespace for the Sparta Tab Utility.
 * @constant {string}
 */
const utilityNamespace = 'spartaTabNavUtility';

/**
 * Collection of namespaced event strings.
 * @constant {object}
 */
const EVENTS = {
  TAB_INITIALIZED: `TAB_INITIALIZED.${utilityNamespace}`,
  TAB_CONTAINER_NOT_FOUND: `TAB_CONTAINER_NOT_FOUND.${utilityNamespace}`,
  TAB_INSTANCE_NOT_FOUND: `TAB_INSTANCE_NOT_FOUND.${utilityNamespace}`
};

// create localized instance of settings
const instance = {};

/**
 * Collection of numeric keycodes for input handlers.
 * @constant {object}
 */
const KEYS = {
  ESCAPE: 27,
  ENTER: 13,
  SPACE: 32,
  TAB: 9,
  UP: 38,
  DOWN: 40,
  RIGHT: 39,
  LEFT: 37,
  SHIFT: 16,
  ALT: 9
};

/**
 * Configures inputs for validation, formatting, obfuscation, etc.
 */
class SpartaTabNavUtility {
  /**
   * Initializes a new instance of the class.
   * @param {object} options - Optional object for configuring overrides.
   * @returns {void}
   */
  init(options = {}) {
    // Initialize `spartaTab`.
    this.spartaTab = [];

    // Get elements.
    const setSelector = options.elementArray || document.querySelectorAll('.spa-tab__container');

    // Kill check.
    if (!setSelector) {
      // eslint-disable-next-line no-console
      console.log(`${EVENTS.TAB_CONTAINER_NOT_FOUND}`);
      return;
    }

    // Loop through any and all tab wrappers.
    Array.prototype.forEach.call(setSelector, element => {
      // Check if this tab set has logic already created for it.
      if (element.getAttribute('data-tab-utility-init') !== 'true') {
        // Create settings object.
        const settings = this.buildSettings(element, options);

        // Add settings to instance so we can reference it in API calls.
        instance[element.id] = settings;

        // If debug mode is on, log merged options.
        if (settings && element.getAttribute('data-debug') === 'true') {
          // eslint-disable-next-line no-console
          console.log(`${utilityNamespace} options:`, settings);
        }

        // On page load and resize, check if controls should be visible
        this.toggleControls(settings);
        this.onPageResize(settings, this.toggleControls, this);
        this.onElementScroll(settings.elements.tabWrapper, settings, this.toggleControls, this);

        // Close all panels on DOM click outside tab wrapper.
        this.resetOnPageClick(settings);

        // Create base click events for tabs.
        this.tabClick(settings);

        // Create base click events for controls
        this.controlsClick(settings);

        // Create base dismissible events.
        this.dismissibleClick(settings);

        // Create base keydown events for tabs.
        this.keyboardSupport(settings);

        // Allow menu to drag with mouse on hold.
        this.dragMenu(settings);

        // If tab is enabled via URL
        this.setActiveByUrl(settings);
        window.addEventListener('hashchange', () => {
          this.setActiveByUrl(settings);
        }, false);

        // Add flag so this tab set can't be re-init.
        element.setAttribute('data-tab-utility-init', 'true');
      }
    });

    // If utility is included, invoke prerender/breakout event listener.
    if (typeof breakOut === 'object') {
      breakOut.init();
    }

    // Publish tab init event. Call via `SpartaTabNavUtility.spartaTab`.
    spartaEventsUtility.pub(EVENTS.TAB_INITIALIZED, this.spartaTab);
  } // END init

  // -------------------------------------------------------- //
  // Core functions below this point.
  // -------------------------------------------------------- //
  /**
   * Window resize event.
   * @param {object} settings - The utility settings object.
   * @param {function} callback - The function to execute on resize.
   * @param {function} utility - Retain access to utility functions within events.
   * @returns {void}
   */
  onPageResize(settings, callback, utility) {
    window.addEventListener('resize', () => {
      if (typeof callback === 'function') {
        callback(settings, utility);
      }
    });
  }

  /**
   * Window resize event.
   * @param {object} element - DOM element to attach listener to.
   * @param {object} settings - The utility settings object.
   * @param {function} callback - The function to execute on resize.
   * @param {function} utility - Retain access to utility functions within events.
   * @returns {void}
   */
  onElementScroll(element, settings, callback, utility) {
    element.addEventListener('scroll', () => {
      if (typeof callback === 'function') {
        callback(settings, utility);
      }
      // Reset all panels.
      if (settings.type === 'navigation') {
        this.resetPanels(settings);
      }
    });
  }

  /**
   * Document click event reset panels.
   * @param {object} settings - The utility settings object.
   * @returns {void}
   */
  resetOnPageClick(settings) {
    // Kill if not navigation tabs
    if (settings.type === 'standard') {
      return;
    }
    document.addEventListener('click', event => {
      if (!settings.root.contains(event.target) && !event.target.hasAttribute('data-tab-target')) {
        this.resetPanels(settings);
      }
    });
    document.addEventListener('keydown', event => {
      if (!settings.root.contains(event.target)) {
        const keyCode = event.charCode || event.keyCode;
        // On escape, reset all.
        if (keyCode === KEYS.ESCAPE) {
          this.resetPanels(settings);
        }
      }
    });
  }

  /**
   * Creates object of DOM elements, classnames, etc to parse throughout utility
   * @param {object} element - DOM element to create settings from.
   * @param {object} options - Object that can override default values in settings.
   * @returns {void}
   */
  buildSettings(element, options) {
    const settings = new function SettingsContructor() {
      this.root = element;
      this.type = this.root.classList.contains('spa-tab--navigation') ? 'navigation' : 'standard';

      // Create variables reachable within any sub-objects.
      const {
        root,
        type
      } = this;
      this.classNames = new function ClassNamesContructor() {
        // DOM elements.
        const classRoot = 'spa-tab__';
        this.tabWrapper = `${classRoot}tab-wrapper`;
        this.panelWrapper = `${classRoot}panel-wrapper`;
        this.tab = `${classRoot}tab-element`;
        this.panel = `${classRoot}list-item-dropdown`;
        this.dismissible = 'spa-card__dismissible';

        // States.
        this.stateActiveGeneral = `${classRoot}state--active`;
        this.stateActiveLoad = `${classRoot}activate-on-load`;
        this.stateActive = `${this.panel}--active`;
        this.stateCurrent = `${this.panel}--current`;
        this.stateDisabled = `${this.panel}--disabled`;

        // Spinner.
        this.dropdownSpinner = `${classRoot}list-item-dropdown-spinner`;
        this.dropdownSpinnerHide = `${this.dropdownSpinner}--hide`;
        this.dropdownSpinnerIcon = `${this.dropdownSpinner}-icon`;
        this.dropdownSpinnerText = `${this.dropdownSpinner}-text`;

        // Spinner icon name and size.
        this.dropdownSpinnerIconName = 'spa-icon-loading';
        this.dropdownSpinnerIconSize = `${this.dropdownSpinnerIconName}-72`;

        // Dropdown.
        this.dropdownLoader = 'spa-card__loader';
        this.dropdownTitle = 'spa-card__title';
        this.dropdownHead = 'spa-card__head-content';
        this.dropdownBody = 'spa-card__body';
        this.dropdownFoot = 'spa-card__foot';

        // Tab controls.
        this.control = `${classRoot}control`;
        this.controlPrev = `${this.control}--prev`;
        this.controlNext = `${this.control}--next`;
        this.controlHidden = `${this.control}--hidden`;
        this.controlHiddenFaux = `${this.control}--hidden-faux`;
      }();
      const {
        classNames
      } = this;
      this.elements = new function ElementsContructor() {
        this.tabWrapper = [...root.getElementsByClassName(classNames.tabWrapper)].length ? [...root.getElementsByClassName(classNames.tabWrapper)][0] : null;
        this.tabs = [...root.getElementsByClassName(classNames.tab)];
        this.panels = [...root.getElementsByClassName(classNames.panel)];
        this.dismissibles = [...root.getElementsByClassName(classNames.dismissible)];
        [this.controlPrev] = [...root.getElementsByClassName(classNames.controlPrev)];
        [this.controlNext] = [...root.getElementsByClassName(classNames.controlNext)];

        // Create array containing tab and panel pairings object.
        // Used for instance and relationship lookups.
        this.tabsAndPanel = [];
        if (this.tabs.length > 0) {
          this.tabButtons = [];
          this.tabs.forEach((tabElm, index) => {
            // get panel from tab
            let thisPanel;
            let thisDismissible;
            let thisLoader;
            if (type === 'navigation') {
              thisPanel = tabElm.nextElementSibling;
            } else {
              const panelID = tabElm.getAttribute('aria-controls');
              thisPanel = this.panels.find(x => x.id === panelID);
            }

            // Get dismissible if panel has one.
            // Get loader if panel has one.
            if (thisPanel) {
              thisDismissible = [...thisPanel.getElementsByClassName(classNames.dismissible)].length ? [...thisPanel.getElementsByClassName(classNames.dismissible)][0] : null;
              thisLoader = thisPanel && thisPanel.getElementsByClassName(classNames.dropdownLoader).length ? thisPanel.getElementsByClassName(classNames.dropdownLoader)[0] : null;
            }

            // Create array of only buttons so we can align it to panel indexes
            if (tabElm.nodeName === 'BUTTON') {
              this.tabButtons.push(tabElm);
            }

            // Build the tab and panel object.
            this.tabsAndPanel[index] = {
              tab: tabElm,
              panel: thisPanel,
              dismissible: thisDismissible,
              dropdown: {
                head: thisPanel && thisPanel.getElementsByClassName(classNames.dropdownHead).length ? thisPanel.getElementsByClassName(classNames.dropdownHead)[0] : null,
                body: thisPanel && thisPanel.getElementsByClassName(classNames.dropdownBody).length ? thisPanel.getElementsByClassName(classNames.dropdownBody)[0] : null,
                foot: thisPanel && thisPanel.getElementsByClassName(classNames.dropdownFoot).length ? thisPanel.getElementsByClassName(classNames.dropdownFoot)[0] : null,
                loader: thisLoader,
                spinner: thisPanel && thisPanel.getAttribute('data-spinner') === 'true',
                spinnerText: thisPanel && thisPanel.getAttribute('data-spinner-text'),
                tabPreserve: thisPanel && thisPanel.getAttribute('data-preserve')
              },
              tabData: JSON.parse(tabElm.getAttribute('data-options'))
            };
            if (thisPanel && thisPanel.getAttribute('data-max-height')) {
              thisPanel.style.maxHeight = thisPanel.getAttribute('data-max-height');
            }

            // Set custom width for tab flyout
            if (thisPanel && (thisPanel.classList.contains('spa-tab__custom--width') || thisPanel.classList.contains('spa-tab__auto--width'))) {
              spartaEventsUtility.sub(`${spartaMediaQueryUtility.EVENTS.BREAKPOINT_CHANGE}:${thisPanel.id}`, () => {
                const viewport = spartaMediaQueryUtility.getCurrentViewport().mediaQueryInfo;
                if (viewport.large && viewport.medium && viewport.small) {
                  const panelWidth = thisPanel.getAttribute('data-max-width');
                  thisPanel.style.maxWidth = panelWidth;
                } else {
                  thisPanel.removeAttribute('style');
                }
              }, true);
            }
          });
        }
      }();
    }();

    // Merge user and default settings together
    return Object.assign(settings, options);
  }

  /**
   * Finds tab instance object based on given ID of tab or panel as string
   * @param {string} elementID - Element ID as a string for DOM lookup
   * @returns {void}
   */
  findInstance(elementID) {
    const settings = {};
    if (typeof elementID === 'string') {
      // Find element in DOM.
      const element = document.getElementById(elementID) || document.getElementById(`tab-${elementID}`) || document.getElementById(`panel-${elementID}`);

      // Ensure an instance object has been set by utility
      if (instance) {
        Object.keys(instance).forEach(key => {
          const tabsArray = instance[key] && instance[key].elements && instance[key].elements.tabsAndPanel ? instance[key].elements.tabsAndPanel : null;

          // Loop through array, find matching instance, build settings object from it.
          if (tabsArray && tabsArray.length) {
            for (let i = 0; i < tabsArray.length; i++) {
              if (tabsArray[i].tab === element || tabsArray[i].panel === element) {
                // Returning object equals that of buildSettings function.
                settings.settings = instance[key];
                for (let a = 0; a < Object.keys(tabsArray[i]).length; a++) {
                  settings[Object.keys(tabsArray[i])[a]] = Object.values(tabsArray[i])[a];
                }
                break;
              }
            }
          }
        });
      } else {
        // eslint-disable-next-line no-console
        console.log(`${EVENTS.TAB_INSTANCE_NOT_FOUND}`);
      }
    }
    return settings;
  }

  /**
   * Update aria label with selected/not selected state based on panel active
   * @param {object} tab - DOM element trigger.
   */
  updateAriaLabel(tab) {
    const ariaExpanded = tab.getAttribute('aria-expanded');
    const hasAriaLabel = tab.hasAttribute('aria-label');
    let ariaLabel = hasAriaLabel ? tab.getAttribute('aria-label') : '';
    ariaLabel = ariaLabel.replace(/(selected)$/, '');
    tab.removeAttribute('aria-label');
    const baseLabel = (tab.getAttribute('data-base-label') || ariaLabel).trim();
    if (!tab.getAttribute('data-base-label')) {
      tab.setAttribute('data-base-label', baseLabel);
    }
    const label = tab.innerText.trim();
    const labelState = 'selected';
    if (ariaExpanded === 'true') {
      tab.setAttribute('aria-label', `${!baseLabel ? label : baseLabel} ${labelState}`);
    } else if (tab.classList.contains('spa-tab__list-item-dropdown--active')) {
      tab.setAttribute('aria-label', `${!baseLabel ? label : baseLabel} ${labelState}`);
    } else {
      tab.removeAttribute('aria-label');
    }
  }

  /**
   * Closes and resets all open panels and tabs.
   * @param {object} settingsObj - Object of instance params.
   * @returns {void}
   */
  resetPanels(settingsObj) {
    const settings = settingsObj;

    // Reset panels.
    settings.elements.panels.forEach(panel => {
      panel.classList.remove(settings.classNames.stateActive);
      if (panel.getAttribute('aria-expanded')) {
        panel.setAttribute('aria-expanded', 'false');
      }
      if (panel.getAttribute('aria-selected')) {
        panel.setAttribute('aria-selected', 'false');
      }
    });

    // Reset tabs.
    settings.elements.tabs.forEach(tab => {
      tab.classList.remove(settings.classNames.stateActive);

      // Reset aria-attributes.
      if (tab.getAttribute('aria-expanded')) {
        tab.setAttribute('aria-expanded', 'false');
        this.updateAriaLabel(tab);
      }
      if (tab.getAttribute('aria-selected')) {
        tab.setAttribute('aria-selected', 'false');
      }
      if (settings.type === 'standard') {
        tab.setAttribute('tabindex', '-1');
      }
      if (tab.classList.contains('spa-tab__activate-by-url')) {
        this.updateAriaLabel(tab);
      }
    });
  }

  /**
   * Opens a targeted panel and sets active state to its tab trigger.
   * @param {object} settingsObj - Object of instance params.
   * @param {object} tabElm - DOM element trigger.
   * @param {object} panelElm - DOM element panel to show.
   * @param {object} panelDismissible - DOM element dismissible.
   * @param {object} tabElmData - JSON supplied by inline data attribute on tab element.
   * @param {boolean} focusTab - Sets focus to tab on panel open.
   * @returns {void}
   */
  openPanel(settingsObj, tabElm, panelElm, panelDismissible, tabElmData, focusTab = true) {
    // If settings is a string, use it to look up settings from tab/panel ID.
    let settingsInstance = {};
    if (typeof settingsObj === 'string') {
      settingsInstance = this.findInstance(settingsObj);
    }

    // Get settings and DOM elements.
    const settings = settingsInstance.settings || settingsObj;
    const tab = settingsInstance.tab || tabElm;
    const panel = settingsInstance.panel || panelElm;
    const dismissible = settingsInstance.dismissible || panelDismissible;
    const tabData = settingsInstance.tabData || tabElmData;
    const {
      callback
    } = settings;

    // Allow toggle.
    if (panel && panel.classList.contains(settings.classNames.stateActive) && settings.type === 'navigation') {
      this.resetPanels(settings);
      return;
    }

    // Reset all panels.
    this.resetPanels(settings);

    // Add active state to clicked panel.
    if (panel) {
      panel.classList.add(settings.classNames.stateActive);
      if (panel.getAttribute('aria-expanded')) {
        panel.setAttribute('aria-expanded', 'true');
      }
      if (panel.getAttribute('aria-selected')) {
        panel.setAttribute('aria-selected', 'true');
      }
    }

    // Add active state to clicked tab.
    if (tab) {
      tab.classList.add(settings.classNames.stateActive);
      if (tab.getAttribute('aria-expanded')) {
        tab.setAttribute('aria-expanded', 'true');
        this.updateAriaLabel(tab);
      }
      if (tab.getAttribute('aria-selected')) {
        tab.setAttribute('aria-selected', 'true');
      }
      if (settings.type === 'standard') {
        tab.removeAttribute('tabindex');
      }
      if (tab.classList.contains('spa-tab__activate-by-url')) {
        this.updateAriaLabel(tab);
      }

      // Align flyout to the clicked tab
      if (panel && (panel.classList.contains('spa-tab__custom--width') || panel.classList.contains('spa-tab__auto--width'))) {
        spartaEventsUtility.sub(`${spartaMediaQueryUtility.EVENTS.BREAKPOINT_CHANGE}:${panel.id}`, () => {
          const viewport = spartaMediaQueryUtility.getCurrentViewport().mediaQueryInfo;
          if (viewport.large && viewport.medium && viewport.small) {
            // Get position of clicked tab
            const tabPos = tab.getBoundingClientRect();

            // Check if there's is enough space on left
            const spaceLeft = tabPos.left;
            const spaceTop = tabPos.bottom;

            // if not enough space then shift to right of clicked tab 
            if (spaceLeft + panel.offsetWidth > window.innerWidth) {
              const panelPos = tabPos.right - panel.offsetWidth;
              if (panelPos > 0) {
                panel.style.left = `${tabPos.right - panel.offsetWidth}px`;
              } else {
                panel.style.left = `${tabPos.right - tabPos.left}px`;
              }
            } else {
              panel.style.left = `${spaceLeft}px`;
            }
            panel.style.top = `${spaceTop}px`;

            // Update top position during scroll
            window.addEventListener('scroll', function () {
              const tabRect = tab.getBoundingClientRect();
              panel.style.top = `${tabRect.bottom}px`;
            });
          }
        }, true);
      }

      // Slide to tab incase it's not visible.
      this.slideToTab(settings, tab, focusTab);
    }

    // If content is loaded dynamically, check flag to see if spinner needs to be displayed.
    if (tab && panel && panel.getAttribute('data-preserve')) {
      spartaTabDropdownUtility.init(settings, this.findInstance(tab.id));
    }

    // Move focus to dismissible on panel open.
    if (dismissible) {
      dismissible.focus();
    }

    // Fire any callback functions.
    if (typeof callback === 'function') {
      callback(settings, tab, panel, tabData);
    }
  }

  /**
   * Actions when a tab is clicked
   * @param {object} settingsObj - Object of instance params.
   * @returns {void}
   */
  tabClick(settingsObj) {
    const settings = settingsObj;
    const tabAndPanel = settings.elements.tabsAndPanel;
    let hasSelected = false;
    if (tabAndPanel.length > 0) {
      tabAndPanel.forEach(object => {
        const {
          tab,
          panel,
          dismissible,
          tabData
        } = object;
        tab.addEventListener('click', () => {
          // Clicking on button tabs.
          if (tab.nodeName === 'BUTTON') {
            // Return allows toggle close if same tab is already open.
            if (panel && panel.classList.contains(settings.classNames.stateActive) && settings.type === 'navigation') {
              this.resetPanels(settings);
              return;
            }

            // Open clicked panel.
            this.openPanel(settings, tab, panel, dismissible, tabData);
          }

          // Clear on load active check class.
          settings.elements.tabs.forEach(tabElm => {
            tabElm.classList.remove(settings.classNames.stateActiveLoad);
          });
        });

        // If tab is currently set to 'selected' on init, open it
        if (tab.getAttribute('aria-selected') === 'true' || tab.classList.contains(settings.classNames.stateActiveLoad)) {
          hasSelected = true;
          if (tab.classList.contains(settings.classNames.stateActiveLoad)) {
            this.openPanel(settings, tab, panel, dismissible, tabData, false);
            tab.removeAttribute('tabindex');
          } else {
            tab.click();
          }
        }
      });

      // If standard tab that has no 'selected' automatically open the first button tab
      if (!hasSelected && settings.type === 'standard') {
        const tabLength = Object.keys(tabAndPanel).length - 1;
        for (let i = 0; i < tabLength; i += 1) {
          if (tabAndPanel[i].tab.nodeName === 'BUTTON') {
            const {
              tab,
              panel,
              dismissible,
              tabData
            } = tabAndPanel[i];
            this.openPanel(settings, tab, panel, dismissible, tabData, false);
            break;
          }

          // If no buttons were found remove tabindex from first tab which should be an anchor.
          if (i === tabLength - 1) {
            tabAndPanel[0].tab.removeAttribute('tabindex');
          }
        }
      }
    }
  }

  /**
   * Actions when a panel dismissible is clicked
   * @param {object} settingsObj - Object of instance params.
   * @returns {void}
   */
  dismissibleClick(settingsObj) {
    const settings = settingsObj;
    if (settings.elements.tabsAndPanel.length > 0) {
      settings.elements.tabsAndPanel.forEach(object => {
        const {
          tab,
          panel,
          dismissible
        } = object;
        if (dismissible) {
          dismissible.addEventListener('click', () => {
            // Return allows toggle close if same tab is already open.
            if (tab && panel) {
              this.resetPanels(settings);
              tab.focus();
            }
          });
          panel.addEventListener('keydown', event => {
            let allowedPress = false;
            const keyCode = event.charCode || event.keyCode;

            // Check if key can have an event run with it.
            Object.keys(KEYS).forEach(key => {
              if (KEYS[key] === keyCode) {
                allowedPress = true;
              }
            });
            if (allowedPress) {
              // On escape, reset all.
              if (keyCode === KEYS.ESCAPE) {
                this.resetPanels(settings);
                tab.focus();
              }
            }
          });
        }
      });
    }
  }

  /**
   * Actions when a keyboard keys are pressed
   * @param {object} settingsObj - Object of instance params.
   * @returns {void}
   */
  keyboardSupport(settingsObj) {
    const settings = settingsObj;
    if (settings.elements.tabsAndPanel.length > 0) {
      settings.elements.tabsAndPanel.forEach(object => {
        const {
          tab,
          panel,
          dismissible,
          tabData
        } = object;

        // Get tab index so we can look up prev/next siblings.
        const index = settings.type === 'navigation' ? Array.from(tab.parentNode.parentNode.children).indexOf(tab.parentNode) : Array.from(tab.parentNode.children).indexOf(tab);

        // Previous and next tab siblings.
        const tabPrev = settings.elements.tabs[index - 1];
        const tabNext = settings.elements.tabs[index + 1];

        // Get tab index so we can look up prev/next siblings.
        const indexPanelPrev = Array.from(settings.elements.tabButtons).indexOf(tabPrev);
        const indexPanelNext = Array.from(settings.elements.tabButtons).indexOf(tabNext);

        // Previous and next panel siblings.
        const panelPrev = settings.elements.panels[indexPanelPrev];
        const panelNext = settings.elements.panels[indexPanelNext];

        // Tab keydown events.
        tab.addEventListener('keydown', event => {
          let allowedPress = false;
          const keyCode = event.charCode || event.keyCode;

          // Check if key can have an event run with it.
          Object.keys(KEYS).forEach(key => {
            if (KEYS[key] === keyCode) {
              allowedPress = true;
            }
          });
          if (allowedPress) {
            // Prevent screen scroll.
            if (keyCode === KEYS.RIGHT || keyCode === KEYS.LEFT) {
              event.preventDefault();
            }

            // On escape, reset all.
            if (keyCode === KEYS.ESCAPE && settings.type === 'navigation') {
              this.resetPanels(settings);
              tab.focus();
            }

            // On tabout of container, reset all.
            if (keyCode === KEYS.TAB && settings.type === 'navigation') {
              const tabContainer = tab.closest('.spa-tab--navigation');
              tabContainer.addEventListener('focusout', event => {
                if (!tabContainer.contains(event.relatedTarget)) {
                  this.resetPanels(settings);
                }
              });
            }

            // On right key down, move to next.
            if (keyCode === KEYS.LEFT && tabPrev) {
              if (settings.type === 'navigation') {
                this.slideToTab(settings, tabPrev);
              }

              // If standard tab, shift focus and activate next panel.
              if (settings.type === 'standard') {
                if (tabPrev.nodeName === 'BUTTON') {
                  this.openPanel(settings, tabPrev, panelPrev, dismissible, tabData);
                  tab.classList.remove(settings.classNames.stateActiveLoad);
                } else {
                  tab.classList.remove(settings.classNames.stateActive);
                  this.slideToTab(settings, tabPrev);
                }
              }
            }

            // On left key down, move to previous.
            if (keyCode === KEYS.RIGHT && tabNext) {
              // If we've arrowed into the last tab for scroll so its visible
              if (settings.type === 'navigation') {
                this.slideToTab(settings, tabNext);
              }

              // If standard tab, shift focus and activate next panel.
              if (settings.type === 'standard') {
                if (tabNext.nodeName === 'BUTTON') {
                  this.openPanel(settings, tabNext, panelNext, dismissible, tabData);
                  tab.classList.remove(settings.classNames.stateActiveLoad);
                } else {
                  tab.classList.remove(settings.classNames.stateActive);
                  this.slideToTab(settings, tabNext);
                }
              }
            }

            // On navigation tab slide scroll to next tab.
            if (keyCode === KEYS.TAB && !(event.shiftKey && event.key === 'Tab') && settings.type === 'navigation' && tabNext && !tab.classList.contains(settings.classNames.stateActive)) {
              event.preventDefault();
              this.slideToTab(settings, tabNext);
            }

            // On navigation tab + shift slide scroll to previous tab.
            if (event.shiftKey && event.key === 'Tab' && settings.type === 'navigation' && tabPrev && !tab.classList.contains(settings.classNames.stateActive)) {
              event.preventDefault();
              this.slideToTab(settings, tabPrev);
            }

            // On standard tab set focus to panel.
            if (keyCode === KEYS.TAB && !(event.shiftKey && event.key === 'Tab') && settings.type === 'standard' && panel) {
              event.preventDefault();
              panel.focus();
            }
          }
        });

        // Panel keydown events.
        if (panel) {
          panel.addEventListener('keydown', event => {
            let allowedPress = false;
            const keyCode = event.charCode || event.keyCode;

            // Check if key can have an event run with it.
            Object.keys(KEYS).forEach(key => {
              if (KEYS[key] === keyCode) {
                allowedPress = true;
              }
            });
            if (allowedPress) {
              // On escape set focus back to tab.
              if (keyCode === KEYS.ESCAPE) {
                // Navigation tabs also reset all tabs.
                if (settings.type === 'navigation') {
                  this.resetPanels(settings);
                }

                // Navigation and standard tabs set focus back to tab.
                tab.focus();
              }

              // On standard shift + tab set focus back to tab.
              if (event.shiftKey && event.key === 'Tab' && settings.type === 'standard' && document.activeElement === panel) {
                event.preventDefault();
                tab.focus();
              }
            }
          });
        }
      });
    }
  }

  /**
   * Enables the user to scroll tabs with mouse hold similar to mobile devices.
   * @param {object} settingsObj - Object of instance params.
   * @param {string} speed - How fast drag action happens.
   * @returns {void}
   */
  dragMenu(settingsObj, speed = 'normal') {
    const settings = settingsObj;
    const menu = settings.elements.tabWrapper;
    let isDown = false;
    let startX;
    let scrollLeft;
    let setSpeed;

    // Determine speed of drag.
    if (speed === 'fast') {
      setSpeed = 3;
    } else if (speed === 'slow') {
      setSpeed = 0.5;
    } else {
      setSpeed = 1;
    }
    menu.addEventListener('mousedown', event => {
      // Kill if mousedown in dropdown area.
      if (event.target.closest('.spa-tab__list-item-dropdown')) {
        isDown = false;
        menu.classList.remove(settings.classNames.stateActiveGeneral);
        return;
      }
      isDown = true;
      menu.classList.add(settings.classNames.stateActiveGeneral);
      startX = event.pageX - menu.offsetLeft;
      ({
        scrollLeft
      } = menu);
    });
    menu.addEventListener('mouseleave', () => {
      isDown = false;
      menu.classList.remove(settings.classNames.stateActiveGeneral);
    });
    menu.addEventListener('mouseup', () => {
      isDown = false;
      menu.classList.remove(settings.classNames.stateActiveGeneral);
    });
    menu.addEventListener('mousemove', event => {
      if (isDown) {
        event.preventDefault();
        const x = event.pageX - menu.offsetLeft;
        const walk = (x - startX) * setSpeed; // Scroll-fast.
        menu.scrollLeft = scrollLeft - walk;
      }
    });
  }

  /**
   * If tab area has scrollbar, show controls.
   * @param {object} settingsObj - Object of instance params.
   * @param {object} utility - Retain utility function access on resize/scroll events.
   * @returns {void}
   */
  toggleControls(settingsObj, utility = this) {
    // Setup.
    const settings = settingsObj;
    const {
      tabWrapper,
      controlPrev,
      controlNext
    } = settings.elements;

    // IE has issues with scrollWidth and clientWidth exact calculations.
    const boundingClientRectWidth = Math.round(tabWrapper.getBoundingClientRect().width);
    const iScrollWidth = Math.round(tabWrapper.scrollWidth);
    const iOffsetWidth = Math.round(tabWrapper.offsetWidth);
    const hasHorizontalScrollbar = iScrollWidth > iOffsetWidth && boundingClientRectWidth === iOffsetWidth;

    // Get scroll position.
    const tabsFirstChild = settings.elements.tabs[0];
    const tabsLastChild = settings.elements.tabs[settings.elements.tabs.length - 1];
    const isVisibleFirst = utility.customBoundaryCheck(tabsFirstChild, tabWrapper);
    const isVisibleLast = utility.customBoundaryCheck(tabsLastChild, tabWrapper);

    // If tab wrapper has scrollbar.
    if (hasHorizontalScrollbar) {
      controlPrev.classList.remove(settings.classNames.controlHidden);
      controlNext.classList.remove(settings.classNames.controlHidden);
    } else {
      controlPrev.classList.add(settings.classNames.controlHidden);
      controlNext.classList.add(settings.classNames.controlHidden);
    }

    // Make control visible when first/last tab is not completely visible.
    if (!isVisibleFirst || !isVisibleLast) {
      // Toggle prev control.
      if (!isVisibleFirst) {
        controlPrev.removeAttribute('aria-hidden');
        controlPrev.removeAttribute('tabindex');
        controlPrev.classList.remove(settings.classNames.controlHiddenFaux);
      } else {
        controlPrev.setAttribute('aria-hidden', 'true');
        controlPrev.setAttribute('tabindex', '-1');
        controlPrev.classList.add(settings.classNames.controlHiddenFaux);
      }

      // Toggle next control.
      if (!isVisibleLast) {
        controlNext.removeAttribute('aria-hidden');
        controlNext.removeAttribute('tabindex');
        controlNext.classList.remove(settings.classNames.controlHiddenFaux);
      } else {
        controlNext.setAttribute('aria-hidden', 'true');
        controlNext.setAttribute('tabindex', '-1');
        controlNext.classList.add(settings.classNames.controlHiddenFaux);
      }
    }
  }

  /**
   * Scrolls tab wrapper width on a control click.
   * @param {object} settingsObj - Object of instance params.
   * @returns {void}
   */
  controlsClick(settingsObj) {
    const settings = settingsObj;
    const {
      tabWrapper,
      controlPrev,
      controlNext
    } = settings.elements;
    const self = this;
    [controlPrev, controlNext].forEach(control => {
      control.addEventListener('click', () => {
        // If control is hidden, don't trigger event.
        if (control.classList.contains(settings.classNames.controlHiddenFaux) || control.classList.contains(settings.classNames.controlHidden)) {
          return;
        }

        // Stop current animation.
        clearInterval(window.slideTimer);

        // Trigger new animation.
        const {
          width
        } = tabWrapper.getBoundingClientRect();
        const speed = control.getAttribute('data-tab-control-speed');
        const step = control.getAttribute('data-tab-control-increment');
        const direction = control.classList.contains(settings.classNames.controlPrev) ? 'left' : 'right';

        // Run smooth scroll interval function.
        self.sideScroll(tabWrapper, direction, parseInt(speed, 0), parseInt(width, 0), parseInt(step, 0));
      });
    });
  }

  /**
   * Returns if element is scrolled a px amount away from start or end of element.
   * @param {object} element - Element to test position with.
   * @param {object} source - Element to test another element position against.
   * @param {boolean} isCompletelyVisible - Checks full width of element.
   * @returns {boolean} - Returns if element is in full view compared to parent position.
   */
  customBoundaryCheck(element, source, isCompletelyVisible = false) {
    // Get relative POS for element based around source, not DOM.

    const childPos = element.getBoundingClientRect();
    const parentPos = source.getBoundingClientRect();
    if (childPos.x >= parentPos.x && childPos.x + childPos.width <= parentPos.x + parentPos.width) {
      isCompletelyVisible = true;
    }
    return isCompletelyVisible;
  }

  /**
   * Smooth scrolls tabs on click of control.
   * @param {*} domElement - The element to be scrolled.
   * @param {*} direction - Direction to be scrolled.
   * @param {*} speed - How many scroll increments happen.
   * @param {*} distance - How far to scroll.
   * @param {*} step - How many steps per increments.
   * @param {*} tab - DOM element to set focus to after animation ends.
   * @returns {void}
   */
  sideScroll(domElement, direction, speed, distance, step, tab = null) {
    let scrollAmount = 0;
    const element = domElement;
    window.slideTimer = setInterval(() => {
      if (direction === 'left') {
        element.scrollLeft -= step;
      } else {
        element.scrollLeft += step;
      }
      scrollAmount += step;
      if (scrollAmount >= distance) {
        window.clearInterval(window.slideTimer);
        if (tab) {
          tab.focus();
        }
      }
    }, speed);
  }

  /**
   * Slides to a tab if it is not in view.
   * @param {object} settingsObj - Object of instance params.
   * @param {object} tabElm - DOM element trigger.
   * @param {boolean} focusTab - Sets focus to tab on slide finish.
   * @returns {void}
   */
  slideToTab(settingsObj, tabElm, focusTab = true) {
    // Get settings and DOM elements.
    const settings = settingsObj;
    let tab = tabElm;
    const {
      tabWrapper
    } = settings.elements;
    const control = settings.elements.controlPrev;
    const controlWidth = 48;
    const parentPos = tabWrapper.getBoundingClientRect();
    const childPos = tab.getBoundingClientRect();
    const iOffsetWidth = tabWrapper.offsetWidth;
    const elementLeft = childPos.left - parentPos.left;
    const elementRight = elementLeft + childPos.width;
    Math.round(childPos.right - parentPos.right);
    Math.round(childPos.left - parentPos.left);

    // Kill check if tab is already in view
    if (elementLeft >= 0 && elementRight <= iOffsetWidth) {
      if (focusTab) {
        tab.focus();
      }
      return;
    }

    // Stop current animation.
    clearInterval(window.slideTimer);

    // Trigger new animation.
    let distance = tabElm.offsetLeft - controlWidth - tabWrapper.scrollLeft + controlWidth / 2;
    const speed = control.getAttribute('data-tab-control-speed');
    const step = control.getAttribute('data-tab-control-increment');
    const direction = distance > 0 ? 'right' : 'left';

    // Make tab flush with side of scrollable area based on direction.
    distance = direction === 'right' ? iOffsetWidth - elementLeft - childPos.width - controlWidth / 2 : distance;
    if (!focusTab) {
      tab = null;
    }

    // Run smooth scroll interval function.
    this.sideScroll(tabWrapper, direction, parseInt(speed, 0), parseInt(Math.abs(distance), 0), parseInt(step, 0), tab);
  }

  /**
   * Sets tab to active state based on URL.
   * @param {object} settingsObj - The utility settings object.
   * @returns {void}
   */
  setActiveByUrl(settingsObj) {
    const settings = settingsObj;
    const tabAndPanel = settings.elements.tabsAndPanel;
    if (tabAndPanel.length > 0) {
      for (let i = 0; i < Object.keys(tabAndPanel).length; i += 1) {
        const {
          tab,
          panel,
          dismissible,
          tabData
        } = tabAndPanel[i];

        // Remove active load class.
        tab.classList.remove(settings.classNames.stateActiveLoad);

        // See if URL includes either just hash or path + hash.
        const getUrlValue = tab.getAttribute('href') && document.location.hash.length > 0 ? document.location.hash : `${document.location.pathname}${document.location.hash}`;
        if (
        // Activate by URL.
        tab.classList.contains('spa-tab__activate-by-url') && tab.getAttribute('href') && tab.getAttribute('href').includes(getUrlValue)) {
          this.openPanel(settings, tab, panel, dismissible, tabData, false);
          tab.classList.add(settings.classNames.stateActiveLoad);
        }
      }
    }
  }

  /**
   * Loads dynamic content into DOM. Content may be brought in via promise or direct DOM injection.
   * @param {object} settings - Settings to pass to `spartaTabDropdownUtility`.
   * @param {object} tab - tab DOM element passed to `spartaTabDropdownUtility`.
   * @param {object} panel - panel DOM element passed to `spartaTabDropdownUtility`.
   * @param {object|string} safeString - The markup to pass to `spartaTabDropdownUtility`.
   * @param {object} this - Pass functions to to `spartaTabDropdownUtility`.
   * @returns {void}
   */
  loadContent(settings, tab, panel, safeString) {
    spartaTabDropdownUtility.dropdownLoadContent(settings, tab, panel, safeString, this);
  }
}

// Export new instance of the Sparta Tab Nav Utility.
var index = new SpartaTabNavUtility();

export { index as default };
